import { Clock, Inbox } from 'lucide-react';
import { useMemo } from 'react';
import type { TriageDensity } from '../../hooks/useTriageDensity';
import { getThreadCustomerId, groupThreadsByInbox } from '../../lib/clinic-logic';
import type { EmailThreadMetadata, LabelCategoryMapping, TriageCategory } from '../../types/email';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { Badge } from '../../ui/badge';
import { TriageEmptyState } from './TriageEmptyState';
import { TriageThreadItem, triageListClassName } from './TriageThreadItem';

interface TriageCustomerViewProps {
  threads: EmailThreadMetadata[];
  selectedCustomerId: string | null;
  isLoading: boolean;
  density?: TriageDensity;
  labelMap?: Record<string, string>;
  mappings?: LabelCategoryMapping[];
  categories?: TriageCategory[];
  workspaceMembers?: Array<{ id: number; email: string; fullName: string | null }>;
  onArchive: (threadId: string) => void;
  onDelegate: (threadId: string) => void;
  onCreateTask: (threadId: string) => void;
  onTaskClick: (taskId: number) => void;
  onCardClick: (threadId: string) => void;
  /** Re-run analysis for one thread; shown only when the failure is retryable. */
  onRetryAnalysis?: (threadId: string) => void;
  isReadOnly?: boolean;
}

export function TriageCustomerView({
  threads,
  selectedCustomerId,
  isLoading,
  density,
  labelMap,
  mappings,
  categories,
  workspaceMembers,
  onArchive,
  onDelegate,
  onCreateTask,
  onTaskClick,
  onCardClick,
  onRetryAnalysis,
  isReadOnly,
}: TriageCustomerViewProps) {
  const visibleThreads = useMemo(() => {
    if (!selectedCustomerId) return threads;
    return threads.filter((t) => getThreadCustomerId(t) === selectedCustomerId);
  }, [threads, selectedCustomerId]);

  // Sub-group a customer's threads by which of the business's inboxes they came
  // through, so it's clear this customer emails us at support@ and sales@ rather
  // than a single undifferentiated pile. Only worth the accordion chrome when
  // more than one inbox is actually involved — otherwise it's pure noise.
  const inboxGroups = useMemo(() => groupThreadsByInbox(visibleThreads), [visibleThreads]);
  const groupByInbox = inboxGroups.length > 1;

  const renderThread = (thread: EmailThreadMetadata) => (
    <TriageThreadItem
      key={thread.threadId}
      thread={thread}
      density={density}
      labelMap={labelMap}
      mappings={mappings}
      categories={categories}
      workspaceMembers={workspaceMembers}
      onArchive={onArchive}
      onDelegate={onDelegate}
      onCreateTask={onCreateTask}
      onTaskClick={onTaskClick}
      onCardClick={onCardClick}
      onRetryAnalysis={onRetryAnalysis}
      isReadOnly={isReadOnly}
    />
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Clock className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (visibleThreads.length === 0) {
    return (
      <TriageEmptyState
        title="No threads found"
        description={
          selectedCustomerId
            ? 'No threads found for this customer with the current filters.'
            : 'Try adjusting your filters or connect email accounts to ingest emails.'
        }
      />
    );
  }

  if (!groupByInbox) {
    return <div className={triageListClassName(density)}>{visibleThreads.map(renderThread)}</div>;
  }

  return (
    <Accordion type="multiple" defaultValue={inboxGroups.map((g) => g.accountEmail)}>
      {inboxGroups.map((group) => (
        <AccordionItem key={group.accountEmail} value={group.accountEmail}>
          <AccordionTrigger>
            <div className="flex items-center gap-2">
              <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-semibold">{group.label}</span>
              <Badge variant="outline" className="text-[10px]">
                {group.threads.length}
              </Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className={triageListClassName(density)}>{group.threads.map(renderThread)}</div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
